import {chunk} from 'lodash';

import {readApiModel, readApiModelInIdleCallbackAsync} from '../../helpers/api/apiHelpers';
import {addQueryToPath} from '../../helpers/browser/urlHelpers';
import {AnySchemaType, ApiSchemaType, ApiType, TypeOfSchema} from '../../models/api/commonApiTypes';
import {logger} from '../logger/logger';
import {sendAsync} from './httpBase';
import {HttpEmptyResponseError, HttpError, HttpResponseError} from './httpError';
import {HttpErrorHandlers, HttpHeaders, HttpResponse, HttpSendOptions, RequestMethodsEnum} from './httpTypes';

/*
 * Constants.
 */

export const httpLogger = logger.getLogger('http');

/*
 * Setup.
 */

const defaultErrorHandlers = Object.freeze({
  onUnauthorized: () => {},
  onSessionTimeout: () => {},
  onSessionsMaxReached: () => {},
});

let errorHandlers: HttpErrorHandlers = defaultErrorHandlers;

/** Potentially setup a callback when we hit a 401 error. */
export function registerHttpErrorHandlers(options: Partial<HttpErrorHandlers>) {
  errorHandlers = {
    ...errorHandlers,
    ...options,
  };
}

export function unregisterHttpErrorHandlers() {
  registerHttpErrorHandlers(defaultErrorHandlers);
}

export interface ApiSendOptions extends HttpSendOptions {
  shouldParseInIdleCallback?: boolean;
}

/*
 * GET.
 */

/** GET the resource at the specified path. */
export async function fetchAsync(path: string, options?: HttpSendOptions): Promise<any> {
  try {
    const response = await sendAsync({
      method: RequestMethodsEnum.GET,
      path,
      errorHandlers,
      ...options,
    });

    // If the response is empty, throw an error
    throwIfEmpty(RequestMethodsEnum.GET, path, response);

    return response.data;
  } catch (error) {
    maybeLogHttpError(error);
    throw error;
  }
}

/** GET the resource at the specified path and parse the result. */
export async function fetchApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  options?: ApiSendOptions,
): Promise<TypeOfSchema<T>> {
  // Fetch and parse the response to JSON.
  const response = await fetchAsync(path, options);

  // Parse + validate.
  return readApiModelAfterApiAsync(apiType, response, options?.shouldParseInIdleCallback);
}

export async function fetchApiByIdsAsync<T>(
  apiType: ApiType<ApiSchemaType<ReadonlyArray<T>, any>>,
  path: string,
  ids: ReadonlyArray<number>,
  options?: ApiSendOptions,
): Promise<Array<T>> {
  const chunks = chunk(ids, 100);

  let results: Array<T> = [];

  for (const chunkIds of chunks) {
    const resultsChunk = await fetchApiAsync(
      apiType,
      addQueryToPath(path, {ids: chunkIds.join(',')}),
      options,
    );
    results = [...results, ...resultsChunk];
  }

  return results;
}

/*
 * POST.
 */

/** POST the provided body at the specified path. */
export async function postAsync(path: string, body?: any, options?: HttpSendOptions): Promise<any> {
  try {
    const response = await sendAsync({
      method: RequestMethodsEnum.POST,
      path,
      body,
      errorHandlers,
      ...options,
    });

    // If the response is empty, throw an error
    throwIfEmpty(RequestMethodsEnum.POST, path, response);

    return response.data;
  } catch (error) {
    maybeLogHttpError(error);
    throw error;
  }
}

/** POST the provided body at the specified path and parse the result. */
export async function postApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  body: any,
  options?: ApiSendOptions,
): Promise<TypeOfSchema<T>> {
  // Send the body and parse the response.
  const response = await postAsync(path, body, options);

  // Validate + conform.
  return readApiModelAfterApiAsync(apiType, response, options?.shouldParseInIdleCallback);
}

export async function postFileAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  file: File,
  options?: HttpSendOptions,
): Promise<TypeOfSchema<T>> {
  const headers: HttpHeaders = {
    'Content-Type': 'file-upload',
    'X-Front-Content-Type': file.type || 'application/octet-stream',
  };

  // Combine with the provided options.
  const newOptions = {
    ...options,
    headers: {
      ...(options && options.headers),
      ...headers,
    },
  };

  // POST as usual.
  return postApiAsync(apiType, path, file, newOptions);
}

/*
 * PUT.
 */

/** PUT the provided body at the specified path. */
export async function putAsync(path: string, body: any, options?: HttpSendOptions): Promise<any> {
  try {
    const response = await sendAsync({
      method: RequestMethodsEnum.PUT,
      path,
      body,
      errorHandlers,
      ...options,
    });

    // If the response is empty, throw an error
    throwIfEmpty(RequestMethodsEnum.PUT, path, response);

    return response.data;
  } catch (error) {
    maybeLogHttpError(error);
    throw error;
  }
}

/** PUT the provided body at the specified path and parse the result. */
export async function putApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  body: any,
  options?: ApiSendOptions,
): Promise<TypeOfSchema<T>> {
  // Send the body and parse the response.
  const response = await putAsync(path, body, options);

  // Validate + conform.
  return readApiModelAfterApiAsync(apiType, response, options?.shouldParseInIdleCallback);
}

/*
 * PATCH.
 */

/** PATCH the provided body at the specified path. */
export async function patchAsync(path: string, body: any, options?: HttpSendOptions): Promise<any> {
  try {
    const response = await sendAsync({
      method: RequestMethodsEnum.PATCH,
      path,
      body,
      errorHandlers,
      ...options,
    });

    // If the response is empty, throw an error
    throwIfEmpty(RequestMethodsEnum.PATCH, path, response);

    return response.data;
  } catch (error) {
    maybeLogHttpError(error);
    throw error;
  }
}

/** PATCH the provided body at the specified path and parse the result. */
export async function patchApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  body: any,
  options?: ApiSendOptions,
): Promise<TypeOfSchema<T>> {
  // Send the body and parse the response.
  const response = await patchAsync(path, body, options);

  // Validate + conform.
  return readApiModelAfterApiAsync(apiType, response, options?.shouldParseInIdleCallback);
}

/*
 * DELETE.
 */

/** DELETE the resource at the specified path. */
export async function deleteAsync(path: string, body?: any, options?: HttpSendOptions): Promise<any> {
  try {
    const response = await sendAsync({
      method: RequestMethodsEnum.DELETE,
      path,
      body,
      errorHandlers,
      ...options,
    });

    return response.data;
  } catch (error) {
    maybeLogHttpError(error);
    throw error;
  }
}

/** DELETE the resource at the specified path and parse the result. */
export async function deleteApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  path: string,
  body?: any,
  options?: ApiSendOptions,
): Promise<TypeOfSchema<T>> {
  // Send the body and parse the response.
  const response = await deleteAsync(path, body, options);

  // Validate + conform.
  return readApiModelAfterApiAsync(apiType, response, options?.shouldParseInIdleCallback);
}

/*
 * Helpers.
 */

function readApiModelAfterApiAsync<T extends AnySchemaType>(
  apiType: ApiType<T>,
  response: any,
  inIdle?: boolean,
) {
  if (inIdle) {
    return readApiModelInIdleCallbackAsync(apiType, response);
  }

  return readApiModel(apiType, response);
}

function throwIfEmpty(method: RequestMethodsEnum, path: string, response: HttpResponse) {
  if (response.data || response.status === 204) {
    return;
  }

  throw new HttpEmptyResponseError(method, path, response);
}

function maybeLogHttpError(error: unknown): void {
  if (!(error instanceof HttpError) || error.isLogged) {
    return;
  }

  const logArgs = [
    error.logMessage,
    // Group under `request` property, since `http` is a reserved property in Datadog.
    {request: error.logContext},
  ] as const;

  // Log _all_ HTTP errors for troubleshooting.
  if (error instanceof HttpResponseError && error.status >= 500) {
    httpLogger.error(...logArgs);
  } else if (error instanceof HttpResponseError && error.status >= 400) {
    httpLogger.warn(...logArgs);
  } else {
    httpLogger.info(...logArgs);
  }

  // Prevent this error from being reported to Bugsnag.

  error.isLogged = true;
}
